'use client';

import { c as _c } from "react/compiler-runtime";
import { FeatherBell } from '@subframe/core';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '@/features/auth/contexts/AuthContext';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import type { FonteNotificacoes, NotificacaoItem } from '@/types/notificationBell/types';
import { dropdownClosed, dropdownToggled, fetchNotifications, selectDropdownOpen, selectUnreadCount, selectUnreadCountByEntityType } from '../store';
import { useNotificationStream } from '../hooks/useNotificationStream';
import { NotificationDropdown } from './NotificationDropdown';
const DROPDOWN_WIDTH = 400;
const VIEWPORT_PAD = 8;
interface DropdownCoords {
  top: number;
  left: number;
}
interface NotificationBellProps {
  onlyEntityType?: 'ConversaPendencia';
  onItemClick?: (item: NotificacaoItem) => void;
  /**
   * Quais itens o `onItemClick` atende. Sem ele, todos viram botão; com ele, o item sem destino
   * segue como texto, e um clique nele não o marca como lido sem levar a lugar nenhum.
   */
  itemAtivavel?: (item: NotificacaoItem) => boolean;
  /**
   * `equipeInterna`: o sino não busca nem abre SSE sozinho — quem mantém a caixa é o
   * `useNotificacoesEquipeInternaPolling`, montado uma vez no layout do painel (dois sinos na
   * mesma tela, como no chat-atendimento, não duplicam requisição).
   */
  fonte?: FonteNotificacoes;
}
export function NotificationBell(t0) {
  const $ = _c(45);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    onlyEntityType,
    onItemClick,
    itemAtivavel,
    fonte: t2
  } = t1;
  const fonte = t2 === undefined ? "workspace" : t2;
  const dispatch = useAppDispatch();
  const globalDropdownOpen = useAppSelector(selectDropdownOpen);
  const [localOpen, setLocalOpen] = useState(false);
  const dropdownOpen = onlyEntityType ? localOpen : globalDropdownOpen;
  let t3;
  if ($[2] !== fonte || $[3] !== onlyEntityType) {
    t3 = state => onlyEntityType ? selectUnreadCountByEntityType(state, fonte, onlyEntityType) : selectUnreadCount(state, fonte);
    $[2] = fonte;
    $[3] = onlyEntityType;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const unreadCount = useAppSelector(t3);
  const {
    user
  } = useAuth();
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const panelRef = useRef(null);
  const [coords, setCoords] = useState(null);
  const [mounted, setMounted] = useState(false);
  const sincronizaSozinho = fonte === "workspace";
  const t4 = sincronizaSozinho ? user?.escritorioId ?? null : null;
  let t5;
  if ($[5] !== t4) {
    t5 = {
      escritorioId: t4
    };
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  useNotificationStream(t5);
  let t6;
  if ($[7] !== dispatch || $[8] !== onlyEntityType) {
    t6 = () => {
      if (onlyEntityType) {
        setLocalOpen(false);
      } else {
        dispatch(dropdownClosed());
      }
    };
    $[7] = dispatch;
    $[8] = onlyEntityType;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const requestClose = t6;
  let t7;
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      setMounted(true);
    };
    t8 = [];
    $[10] = t7;
    $[11] = t8;
  } else {
    t7 = $[10];
    t8 = $[11];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[12] !== dispatch || $[13] !== sincronizaSozinho) {
    t9 = () => {
      if (sincronizaSozinho) {
        dispatch(fetchNotifications("workspace"));
      }
    };
    t10 = [dispatch, sincronizaSozinho];
    $[12] = dispatch;
    $[13] = sincronizaSozinho;
    $[14] = t10;
    $[15] = t9;
  } else {
    t10 = $[14];
    t9 = $[15];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[16] !== dropdownOpen) {
    t11 = () => {
      if (!dropdownOpen) {
        setCoords(null);
        return;
      }
      const update = () => {
        const rect = buttonRef.current?.getBoundingClientRect();
        if (!rect) {
          return;
        }
        const preferredLeft = rect.right - DROPDOWN_WIDTH;
        const maxLeft = window.innerWidth - DROPDOWN_WIDTH - VIEWPORT_PAD;
        const left = Math.max(VIEWPORT_PAD, Math.min(preferredLeft, maxLeft));
        setCoords({
          top: rect.bottom + 10,
          left
        });
      };
      update();
      window.addEventListener("resize", update);
      window.addEventListener("scroll", update, true);
      return () => {
        window.removeEventListener("resize", update);
        window.removeEventListener("scroll", update, true);
      };
    };
    t12 = [dropdownOpen];
    $[16] = dropdownOpen;
    $[17] = t11;
    $[18] = t12;
  } else {
    t11 = $[17];
    t12 = $[18];
  }
  useLayoutEffect(t11, t12);
  let t13;
  let t14;
  if ($[19] !== dropdownOpen || $[20] !== requestClose) {
    t13 = () => {
      if (!dropdownOpen) {
        return;
      }
      const handler = e => {
        const target = e.target as Node;
        if (rootRef.current?.contains(target)) {
          return;
        }
        if (panelRef.current?.contains(target)) {
          return;
        }
        requestClose();
      };
      document.addEventListener("mousedown", handler);
      return () => document.removeEventListener("mousedown", handler);
    };
    t14 = [dropdownOpen, requestClose];
    $[19] = dropdownOpen;
    $[20] = requestClose;
    $[21] = t13;
    $[22] = t14;
  } else {
    t13 = $[21];
    t14 = $[22];
  }
  useEffect(t13, t14);
  let t15;
  if ($[23] !== coords || $[24] !== dropdownOpen || $[25] !== fonte || $[26] !== itemAtivavel || $[27] !== mounted || $[28] !== onItemClick || $[29] !== onlyEntityType || $[30] !== requestClose) {
    t15 = mounted && dropdownOpen && coords ? createPortal(<div ref={panelRef} className="fixed z-[9999]" style={{
      top: coords.top,
      left: coords.left
    }}><NotificationDropdown fonte={fonte} onlyEntityType={onlyEntityType} onItemClick={onItemClick} itemAtivavel={itemAtivavel} onRequestClose={requestClose} /></div>, document.body) : null;
    $[23] = coords;
    $[24] = dropdownOpen;
    $[25] = fonte;
    $[26] = itemAtivavel;
    $[27] = mounted;
    $[28] = onItemClick;
    $[29] = onlyEntityType;
    $[30] = requestClose;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  const dropdown = t15;
  let t16;
  if ($[32] !== dispatch || $[33] !== onlyEntityType) {
    t16 = () => {
      if (onlyEntityType) {
        setLocalOpen(_temp);
      } else {
        dispatch(dropdownToggled());
      }
    };
    $[32] = dispatch;
    $[33] = onlyEntityType;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  const t17 = `relative flex h-9 w-9 items-center justify-center rounded-[8px] border-0 transition-colors ${dropdownOpen ? "text-default-font bg-neutral-100" : "bg-transparent text-neutral-500 hover:bg-neutral-50"}`;
  let t18;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <FeatherBell className="h-[18px] w-[18px]" />;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  let t19;
  if ($[36] !== unreadCount) {
    t19 = unreadCount > 0 && <span className="bg-error-600 absolute top-1 right-1 flex h-4 min-w-[16px] items-center justify-center rounded-full px-[3px] text-[9px] leading-none font-bold text-white ring-2 ring-white">{unreadCount > 99 ? "99+" : unreadCount}</span>;
    $[36] = unreadCount;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  let t20;
  if ($[38] !== t16 || $[39] !== t17 || $[40] !== t19) {
    t20 = <button ref={buttonRef} type="button" onClick={t16} aria-label={"Notifica\xE7\xF5es"} className={t17}>{t18}{t19}</button>;
    $[38] = t16;
    $[39] = t17;
    $[40] = t19;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] !== dropdown || $[43] !== t20) {
    t21 = <div ref={rootRef} className="relative">{t20}{dropdown}</div>;
    $[42] = dropdown;
    $[43] = t20;
    $[44] = t21;
  } else {
    t21 = $[44];
  }
  return t21;
}
function _temp(open) {
  return !open;
}